import React, {Fragment, useState} from "react";
import {Button, Modal, ModalBody, ModalFooter, ModalHeader, Table} from "reactstrap";
import {toast} from "react-toastify";

const formatMd = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
        const parts = [];
        let rest = line, k = 0;
        while (rest.length > 0) {
            const m = rest.match(/\*\*(.+?)\*\*/);
            if (m) {
                const idx = rest.indexOf(m[0]);
                if (idx > 0) parts.push(<span key={k++}>{rest.substring(0, idx)}</span>);
                parts.push(<strong key={k++}>{m[1]}</strong>);
                rest = rest.substring(idx + m[0].length);
                continue;
            }
            parts.push(<span key={k++}>{rest}</span>);
            break;
        }
        return <React.Fragment key={i}>{parts}{i < text.split('\n').length - 1 && <br/>}</React.Fragment>;
    });
};

const prefixTablesWithDb = (sql, dbName) => {
    if (!sql || !dbName) return sql;
    return sql.replace(
        /(\b(?:FROM|JOIN)\s+)`?(\w+)`?/gi,
        (match, keyword, tableName) => {
            if (match.includes('.')) return match;
            return `${keyword}\`${dbName}\`.\`${tableName}\``;
        }
    );
};

const TAB_RESULTS = 'results';
const TAB_CONVERSATION = 'conversation';
const TAB_QUERY = 'query';

const ReportPreview = ({isOpen, toggle, report, dbName}) => {
    const [activeTab, setActiveTab] = useState(TAB_RESULTS);

    if (!report) return null;

    const previewData = report.preview_data || [];
    const headings = previewData.length > 0 ? Object.keys(previewData[0]) : [];
    const conversationMessages = report.conversation_messages || [];
    const rawSql = report.generated_sql || '';
    const displaySql = prefixTablesWithDb(rawSql, dbName);

    const handleDownload = () => {
        if (report.file_url) {
            const link = document.createElement("a");
            link.href = report.file_url;
            link.setAttribute("download", "");
            document.body.appendChild(link);
            link.click();
            link.remove();
        }
    };

    const handleCopySql = () => {
        if (!displaySql) return;
        navigator.clipboard.writeText(displaySql).then(() => {
            toast.success('Query copied to clipboard', {position: toast.POSITION.TOP_RIGHT, autoClose: 2000});
        }).catch(() => {
            const ta = document.createElement('textarea');
            ta.value = displaySql;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            toast.success('Query copied to clipboard', {position: toast.POSITION.TOP_RIGHT, autoClose: 2000});
        });
    };

    const tabs = [
        {id: TAB_RESULTS, label: 'Results', icon: 'fa-table'},
        {id: TAB_CONVERSATION, label: 'Conversation', icon: 'fa-comments-o'},
        {id: TAB_QUERY, label: 'Database Query', icon: 'fa-code'},
    ];

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable onOpened={() => setActiveTab(TAB_RESULTS)}>
            {/* Header */}
            <div style={{
                padding: '16px 24px', borderBottom: '1px solid #edf2f7',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
                <div>
                    <div style={{fontSize: 16, fontWeight: 600, color: '#1a202c', lineHeight: 1.3}}>
                        {report.report_name || 'Report Preview'}
                    </div>
                    <div style={{fontSize: 12, color: '#718096', marginTop: 2, display: 'flex', gap: 12}}>
                        {report.row_count != null && <span>{report.row_count.toLocaleString()} rows</span>}
                        {report.execution_time_ms && <span>{(report.execution_time_ms / 1000).toFixed(1)}s</span>}
                        {report.status && (
                            <span style={{
                                color: report.status === 'completed' ? '#38a169' : report.status === 'failed' ? '#e53e3e' : '#718096',
                                fontWeight: 500,
                                textTransform: 'capitalize',
                            }}>{report.status}</span>
                        )}
                    </div>
                </div>
                <button onClick={toggle} style={{
                    background: 'none', border: 'none', fontSize: 20, color: '#a0aec0',
                    cursor: 'pointer', padding: '4px 8px', lineHeight: 1,
                }}>
                    <i className="fa fa-times"></i>
                </button>
            </div>

            {/* Tabs */}
            <div style={{
                display: 'flex', borderBottom: '2px solid #edf2f7',
                backgroundColor: '#fff', padding: '0 24px',
            }}>
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        style={{
                            padding: '12px 20px',
                            fontSize: 13, fontWeight: 500,
                            color: activeTab === tab.id ? '#4c6ef5' : '#718096',
                            background: 'none', border: 'none', borderBottom: '2px solid transparent',
                            marginBottom: -2, cursor: 'pointer',
                            borderBottomColor: activeTab === tab.id ? '#4c6ef5' : 'transparent',
                            transition: 'all 0.15s ease',
                            display: 'flex', alignItems: 'center', gap: 6,
                        }}
                    >
                        <i className={`fa ${tab.icon}`} style={{fontSize: 12}}></i>
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <ModalBody style={{padding: 0, minHeight: 350}}>
                {/* Results Tab */}
                {activeTab === TAB_RESULTS && (
                    <div style={{padding: 24}}>
                        {report.status === 'failed' && (
                            <div style={{
                                padding: '12px 16px', borderRadius: 8, fontSize: 13,
                                backgroundColor: '#fff5f5', border: '1px solid #feb2b2', color: '#c53030',
                            }}>
                                {report.error_message || 'Report generation failed.'}
                            </div>
                        )}
                        {report.status === 'completed' && report.error_message && (
                            <div style={{
                                padding: '12px 16px', borderRadius: 8, fontSize: 13, marginBottom: 16,
                                backgroundColor: '#fffaf0', border: '1px solid #fbd38d', color: '#c05621',
                            }}>
                                {report.error_message}
                            </div>
                        )}
                        {report.status === 'completed' && previewData.length > 0 && (
                            <Fragment>
                                <div style={{fontSize: 12, color: '#718096', marginBottom: 12}}>
                                    Showing first {previewData.length} of {report.row_count != null ? report.row_count.toLocaleString() : '?'} rows
                                </div>
                                <div style={{
                                    maxHeight: 420, overflow: 'auto',
                                    border: '1px solid #e2e8f0', borderRadius: 8,
                                }}>
                                    <Table bordered size="sm" style={{marginBottom: 0, fontSize: 12}}>
                                        <thead>
                                            <tr style={{backgroundColor: '#f7fafc'}}>
                                                {headings.map((h, i) => (
                                                    <th key={i} style={{
                                                        whiteSpace: 'nowrap', padding: '10px 12px',
                                                        fontSize: 11, fontWeight: 600, color: '#4a5568',
                                                        position: 'sticky', top: 0, backgroundColor: '#f7fafc',
                                                        borderBottom: '2px solid #e2e8f0',
                                                    }}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {previewData.map((row, ri) => (
                                                <tr key={ri} style={{backgroundColor: ri % 2 === 0 ? '#fff' : '#fafbfc'}}>
                                                    {headings.map((h, ci) => (
                                                        <td key={ci} style={{
                                                            whiteSpace: 'nowrap', padding: '8px 12px',
                                                            fontSize: 12, color: '#2d3748',
                                                        }}>
                                                            {row[h] !== null ? String(row[h]) : ''}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Fragment>
                        )}
                        {report.status === 'completed' && previewData.length === 0 && (
                            <div style={{textAlign: 'center', padding: '40px 0', color: '#a0aec0'}}>
                                <i className="fa fa-inbox" style={{fontSize: 32, marginBottom: 8, display: 'block'}}></i>
                                <span style={{fontSize: 13}}>No preview data available</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Conversation Tab */}
                {activeTab === TAB_CONVERSATION && (
                    <div style={{padding: 24}}>
                        {conversationMessages.length > 0 ? (
                            <div style={{maxHeight: 420, overflowY: 'auto'}}>
                                {conversationMessages.map((msg, idx) => (
                                    <div key={idx} style={{
                                        display: 'flex',
                                        justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                        marginBottom: 14,
                                    }}>
                                        {msg.role === 'assistant' && (
                                            <div style={{
                                                width: 26, height: 26, borderRadius: 6, flexShrink: 0,
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: '#fff', fontSize: 10, marginRight: 8, marginTop: 2,
                                            }}>
                                                <i className="fa fa-magic"></i>
                                            </div>
                                        )}
                                        <div style={{
                                            maxWidth: '72%',
                                            padding: '10px 14px',
                                            borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                                            backgroundColor: msg.role === 'user' ? '#4c6ef5' : '#f7fafc',
                                            color: msg.role === 'user' ? '#fff' : '#2d3748',
                                            border: msg.role === 'assistant' ? '1px solid #e2e8f0' : 'none',
                                            fontSize: 12.5, lineHeight: 1.6, wordBreak: 'break-word',
                                        }}>
                                            {msg.role === 'assistant' ? formatMd(msg.content) : msg.content}
                                        </div>
                                        {msg.role === 'user' && (
                                            <div style={{
                                                width: 26, height: 26, borderRadius: 6, flexShrink: 0,
                                                backgroundColor: '#4c6ef5',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: '#fff', fontSize: 10, marginLeft: 8, marginTop: 2,
                                            }}>
                                                <i className="fa fa-user"></i>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{textAlign: 'center', padding: '40px 0', color: '#a0aec0'}}>
                                <i className="fa fa-comments-o" style={{fontSize: 32, marginBottom: 8, display: 'block'}}></i>
                                <span style={{fontSize: 13}}>No conversation history available</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Query Tab */}
                {activeTab === TAB_QUERY && (
                    <div style={{padding: 24}}>
                        {displaySql ? (
                            <Fragment>
                                <div style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    marginBottom: 12,
                                }}>
                                    <span style={{fontSize: 12, color: '#718096', fontWeight: 500}}>
                                        Generated SQL Query
                                    </span>
                                    <button
                                        onClick={handleCopySql}
                                        style={{
                                            border: '1px solid #e2e8f0', borderRadius: 6,
                                            padding: '6px 14px', fontSize: 12, fontWeight: 500,
                                            background: '#fff', color: '#4a5568', cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', gap: 6,
                                            transition: 'all 0.15s ease',
                                        }}
                                        onMouseEnter={e => { e.target.style.backgroundColor = '#4c6ef5'; e.target.style.color = '#fff'; e.target.style.borderColor = '#4c6ef5'; }}
                                        onMouseLeave={e => { e.target.style.backgroundColor = '#fff'; e.target.style.color = '#4a5568'; e.target.style.borderColor = '#e2e8f0'; }}
                                    >
                                        <i className="fa fa-clipboard"></i> Copy
                                    </button>
                                </div>
                                <pre style={{
                                    backgroundColor: '#1e1e2e',
                                    color: '#cdd6f4',
                                    padding: 20,
                                    borderRadius: 10,
                                    fontSize: 12.5,
                                    lineHeight: 1.8,
                                    overflow: 'auto',
                                    maxHeight: 400,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
                                    margin: 0,
                                    border: '1px solid #313244',
                                }}>
                                    {displaySql}
                                </pre>
                            </Fragment>
                        ) : (
                            <div style={{textAlign: 'center', padding: '40px 0', color: '#a0aec0'}}>
                                <i className="fa fa-code" style={{fontSize: 32, marginBottom: 8, display: 'block'}}></i>
                                <span style={{fontSize: 13}}>No query available for this report</span>
                            </div>
                        )}
                    </div>
                )}
            </ModalBody>

            {/* Footer */}
            <div style={{
                padding: '12px 24px', borderTop: '1px solid #edf2f7',
                display: 'flex', justifyContent: 'flex-end', gap: 8,
                backgroundColor: '#fff',
            }}>
                {report.status === 'completed' && report.file_url && (
                    <button onClick={handleDownload} style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: '#fff', border: 'none', borderRadius: 8,
                        padding: '8px 18px', fontSize: 13, fontWeight: 500,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                        <i className="fa fa-download"></i> Download Excel
                    </button>
                )}
                <button onClick={toggle} style={{
                    background: '#fff', color: '#4a5568',
                    border: '1px solid #e2e8f0', borderRadius: 8,
                    padding: '8px 18px', fontSize: 13, fontWeight: 500,
                    cursor: 'pointer',
                }}>
                    Close
                </button>
            </div>
        </Modal>
    );
};

export default ReportPreview;
